import type React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getUser } from '@/lib/auth';
import { getVerticalAdminAccess } from '@/lib/verticalAccess';
import { surfacesApi } from '@/api/surfaces';
import { homePathFor } from '@/lib/surfaceManifest';
import { NoticeRedirect } from './RouteNotice';

type GrantState =
  | { status: 'granted' }
  | { status: 'denied'; homePath: string }
  | null;

// Generalized operator gate for the vertical management center
// (VERTICAL-PRICING-ADMIN-1 + founder handover CTO-MASTER-OPERATOR-HANDOVER-1).
// Reads the `:vertical` route param and admits:
//   1. a server-confirmed Director (any vertical) or a static-operator tenant of
//      THIS vertical — the synchronous fast path (no fetch); OR
//   2. a founder holding a RUNTIME operator grant — resolved authoritatively from
//      GET /me/surfaces `manageable_verticals` (the same backend resolver the
//      admin API gates on). A grant is not visible to the tenant-role check, so
//      this second path is what makes founder handover work end-to-end.
// The backend re-enforces the same scope on every action; this is the UX gate.
export function RequireVerticalOperator({ children }: { children: React.ReactNode }) {
  const { vertical = '' } = useParams();
  const syncAccess = getVerticalAdminAccess(getUser(), vertical);

  // Granted / denied-with-authorized-home / null (still checking). Only consulted
  // when the synchronous path did not already admit.
  const [grantState, setGrantState] = useState<GrantState>(
    syncAccess.canView ? { status: 'granted' } : null,
  );

  useEffect(() => {
    if (syncAccess.canView) {
      setGrantState({ status: 'granted' });
      return;
    }
    let cancelled = false;
    setGrantState(null);
    surfacesApi
      .getMySurfaces()
      .then((s) => {
        if (cancelled) return;
        const manages = (s.manageable_verticals ?? []).map((v) => v.toLowerCase());
        setGrantState(
          manages.includes(vertical.toLowerCase())
            ? { status: 'granted' }
            : {
                status: 'denied',
                homePath: homePathFor(new Set(s.visible_surfaces)),
              },
        );
      })
      .catch(() => {
        if (!cancelled) setGrantState({ status: 'denied', homePath: '/research' });
      });
    return () => {
      cancelled = true;
    };
  }, [vertical, syncAccess.canView]);

  if (grantState === null) {
    // Brief check against the authoritative surface resolver (granted founders).
    return (
      <div className="flex h-full min-h-[40vh] items-center justify-center text-sm text-gray-400">
        Checking operator access…
      </div>
    );
  }

  if (grantState.status === 'denied') {
    return <NoticeRedirect to={grantState.homePath} message={syncAccess.reason} />;
  }

  return <>{children}</>;
}
